import type { IRatingMatrixFrameworkParameter, IRatingMatrixFrameworkSubParameter } from '__generated__';
import {
  baseApi,
  parameterListTag,
  subParameterListTag,
  templateDetailTag,
} from 'store/api';
import {
  API,
  APIRead,
  asPatchBody,
  getAllDynamic,
  normalizeError,
  type PatchBody,
} from 'utils/api-client';
import { buildSubParametersForTemplateFilter } from 'services/rating-matrix-framework-sub-parameter/helpers';
import {
  buildParameterTemplateFilter,
  mergeParametersWithSubParameters,
} from './helpers';

async function fetchSubParametersForTemplate(
  parameterIds: number[],
): Promise<IRatingMatrixFrameworkSubParameter[]> {
  const filter = buildSubParametersForTemplateFilter(parameterIds);

  if (!filter) {
    return [];
  }

  const { value } = await getAllDynamic<IRatingMatrixFrameworkSubParameter>(
    (body) => APIRead.api.ratingMatrixFrameworkSubParameterGetAllDynamicCreate(body),
    {
      filter,
      orderBy: 'groupSortOrder asc, sortOrder asc',
    },
  );

  return value;
}

export const ratingMatrixFrameworkParameterApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getParametersTree: builder.query<
      IRatingMatrixFrameworkParameter[],
      { frameworkTemplateID: number }
    >({
      async queryFn({ frameworkTemplateID }) {
        try {
          const { value: parameters } = await getAllDynamic<IRatingMatrixFrameworkParameter>(
            (body) => APIRead.api.ratingMatrixFrameworkParameterGetAllDynamicCreate(body),
            {
              filter: buildParameterTemplateFilter(frameworkTemplateID),
              orderBy: 'categorySortOrder asc, sortOrder asc',
            },
          );

          const parameterIds = parameters.map((parameter) => parameter.id);
          const subParameters = await fetchSubParametersForTemplate(parameterIds);

          return {
            data: mergeParametersWithSubParameters(parameters, subParameters),
          };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: (_result, _error, arg) => [
        parameterListTag(arg.frameworkTemplateID),
        subParameterListTag(arg.frameworkTemplateID),
      ],
    }),

    createParameter: builder.mutation<
      IRatingMatrixFrameworkParameter,
      Partial<IRatingMatrixFrameworkParameter> & { frameworkTemplateID: number }
    >({
      async queryFn(body) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkParameterCreateCreate(body);
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        parameterListTag(arg.frameworkTemplateID),
        subParameterListTag(arg.frameworkTemplateID),
        templateDetailTag(arg.frameworkTemplateID),
      ],
    }),

    patchParameter: builder.mutation<
      string,
      {
        id: number;
        frameworkTemplateID: number;
        patch: PatchBody<IRatingMatrixFrameworkParameter>;
      }
    >({
      async queryFn({ id, patch }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkParameterPatchPartialUpdate(
            { id },
            asPatchBody(patch),
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        parameterListTag(arg.frameworkTemplateID),
        templateDetailTag(arg.frameworkTemplateID),
      ],
    }),

    deleteParameter: builder.mutation<
      IRatingMatrixFrameworkParameter,
      { id: number; frameworkTemplateID: number }
    >({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkParameterDeleteDelete({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        parameterListTag(arg.frameworkTemplateID),
        subParameterListTag(arg.frameworkTemplateID),
        templateDetailTag(arg.frameworkTemplateID),
      ],
    }),
  }),
});

export const {
  useGetParametersTreeQuery,
  useCreateParameterMutation,
  usePatchParameterMutation,
  useDeleteParameterMutation,
} = ratingMatrixFrameworkParameterApi;
